import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateKnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, RefreshCw, User, Building, Mic } from "lucide-react";
import fileService, { KnowledgeItem } from "@/services/fileService";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useCombinedAgentList } from "@/hooks/agent/useCombinedAgentList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const snippetSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  content: z.string().min(1, "Content is required"),
});

interface AddSnippetFormProps {
  onSuccess: (item?: KnowledgeItem) => void;
  agentId?: string | null;
  renderButton?: (props: {
    isLoading: boolean;
    isAgentSpecific: boolean;
    selectedAgentId: string | null;
    getSelectedAgentName: () => string | null;
    isAiProcessing: boolean;
  }) => React.ReactNode;
}

const AddSnippetForm = ({
  onSuccess,
  agentId: initialAgentId,
  renderButton,
}: AddSnippetFormProps) => {
  const { createItem, isLoading: isCreatingItem } = useCreateKnowledgeItem();
  const { agents, isLoading: isAgentsLoading } = useCombinedAgentList();
  const { organization, currentOrganization } = useAuth();
  const { toast } = useToast();
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiProcessingFailed, setAiProcessingFailed] = useState(false);
  const [savedItemData, setSavedItemData] = useState<{
    id: string;
    title: string;
    content: string;
  } | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Agent selection state
  const [isAgentSpecific, setIsAgentSpecific] = useState(!!initialAgentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    initialAgentId || null,
  );

  // Get the organization ID from either organization or currentOrganization
  const orgId = organization?.id || currentOrganization?.id;

  const form = useForm<z.infer<typeof snippetSchema>>({
    resolver: zodResolver(snippetSchema),
    defaultValues: {
      title: "",
      description: "",
      content: "",
    },
  });

  const handleAgentSpecificToggle = (checked: boolean) => {
    setIsAgentSpecific(checked);
    if (!checked) {
      setSelectedAgentId(null);
    }
  };

  const processSnippetForAI = async (
    title: string,
    content: string,
    itemId: string,
  ) => {
    if (!orgId) return false;

    try {
      setIsRetrying(true);

      // Use the unified knowledge processing system
      const result = await fileService.processKnowledgeItem(itemId, orgId);

      if (!result.success) {
        console.error("Failed to process knowledge item:", result.error);
        throw new Error(result.error);
      }

      console.log(`Successfully processed knowledge item ${itemId} for AI`);

      // Clear error state and saved item data
      setAiProcessingFailed(false);
      setSavedItemData(null);

      toast({
        title: "AI processing successful",
        description: "Your snippet is now searchable by AI agents.",
      });

      return true;
    } catch (error) {
      console.error("Error processing snippet for AI:", error);
      setAiProcessingFailed(true);

      toast({
        variant: "destructive",
        title: "Error processing for AI",
        description:
          "Your snippet was saved, but could not be made searchable by AI. You can retry later.",
      });

      return false;
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRetry = async () => {
    if (!savedItemData || !orgId) return;

    await processSnippetForAI(
      savedItemData.title,
      savedItemData.content,
      savedItemData.id,
    );
  };

  const onSubmit = async (values: z.infer<typeof snippetSchema>) => {
    if (!orgId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No organization found",
      });
      return;
    }

    // Validate agent selection if agent-specific is enabled
    if (isAgentSpecific && !selectedAgentId) {
      toast({
        variant: "destructive",
        title: "Agent required",
        description:
          "Please select an agent or switch to organization-level snippet",
      });
      return;
    }

    // Determine final agent ID and type
    const finalAgentId = isAgentSpecific ? selectedAgentId : null;
    const selectedAgent = finalAgentId ? agents.find(a => a.id === finalAgentId) : null;
    const isVoiceAgent = selectedAgent?.type === "voice";

    // First, create the knowledge item in the database
    const result = await createItem({
      title: values.title,
      description: values.description || null,
      content: values.content,
      type: "snippet",
      ...(isVoiceAgent 
        ? { voice_agent_id: finalAgentId }
        : { agent_id: finalAgentId }
      ),
    });

    if (result) {
      // Create KnowledgeItem object to pass back to parent
      const item: KnowledgeItem = {
        id: result.id,
        title: values.title,
        description: values.description || null,
        type: "snippet",
        content: values.content,
        organization_id: orgId || "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ai_processed: false,
        agent_id: finalAgentId,
      };

      // Store data for possible retry
      setSavedItemData({
        id: result.id,
        title: values.title,
        content: values.content,
      });

      // Always process for AI searchability (default enabled)
      if (orgId) {
        setIsAiProcessing(true);

        try {
          const success = await processSnippetForAI(
            values.title,
            values.content,
            result.id,
          );

          // Update the ai_processed status
          if (success) {
            item.ai_processed = true;

            toast({
              title: "Snippet created and indexed",
              description: `Your snippet has been created and made searchable by ${finalAgentId ? "your agent" : "all agents in your organization"}.`,
            });
          } else {
            toast({
              title: "Snippet created",
              description:
                "Your snippet was saved but couldn't be indexed for AI search. You can retry later.",
            });
          }
        } catch (error) {
          console.error("Error processing snippet for AI:", error);
          setAiProcessingFailed(true);
          toast({
            title: "Snippet created",
            description:
              "Your snippet was saved but couldn't be indexed for AI search. You can retry later.",
          });
        } finally {
          setIsAiProcessing(false);
        }
      } else {
        toast({
          title: "Snippet created",
          description: "Your snippet has been created successfully.",
        });
      }

      form.reset();
      setIsAgentSpecific(!!initialAgentId);
      setSelectedAgentId(initialAgentId || null);
      onSuccess(item);
    }
  };

  const getSelectedAgentName = () => {
    if (!selectedAgentId) return null;
    const agent = agents.find((a) => a.id === selectedAgentId);
    return agent ? agent.title : "Unknown Agent";
  };

  const isLoading = isCreatingItem || isAiProcessing;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {aiProcessingFailed && savedItemData && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>AI Processing Failed</AlertTitle>
            <AlertDescription>
              Your snippet was saved but couldn't be processed for AI search.
              This means our AI agents won't be able to search its contents.
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  disabled={isRetrying || !orgId}
                  className="mt-2"
                >
                  {isRetrying ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry AI Processing
                    </>
                  )}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Agent Selection Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="agent-specific" className="text-sm font-medium">
                Create for specific agent
              </Label>
              <p className="text-xs text-muted-foreground">
                Choose whether this snippet should be available to all agents or
                just one specific agent
              </p>
            </div>
            <Switch
              id="agent-specific"
              checked={isAgentSpecific}
              onCheckedChange={handleAgentSpecificToggle}
              disabled={isAgentsLoading}
            />
          </div>

          {isAgentSpecific ? (
            <div className="space-y-2">
              <Label className="text-sm">Select Agent</Label>
              <Select
                value={selectedAgentId || ""}
                onValueChange={setSelectedAgentId}
                disabled={isAgentsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center">
                        {agent.type === "voice" ? (
                          <Mic className="mr-2 h-4 w-4 text-blue-600" />
                        ) : (
                          <User className="mr-2 h-4 w-4" />
                        )}
                        {agent.title}
                        {agent.type === "voice" && (
                          <span className="ml-2 text-xs text-muted-foreground">(Voice)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAgentId && (
                <p className="text-xs text-muted-foreground flex items-center">
                  <User className="mr-1 h-3 w-3" />
                  Will be available only to: {getSelectedAgentName()}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center text-xs text-muted-foreground">
              <Building className="mr-1 h-3 w-3" />
              Will be available to all agents in your organization
            </div>
          )}
        </div>

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter a title for this snippet"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add a brief description of this snippet"
                  className="h-20"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter the content for this snippet"
                  className="min-h-[200px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Render button if renderButton prop is provided */}
        {renderButton &&
          renderButton({
            isLoading,
            isAgentSpecific,
            selectedAgentId,
            getSelectedAgentName,
            isAiProcessing,
          })}
      </form>
    </Form>
  );
};

export default AddSnippetForm;
